/**
 * CKEditor 5 upload adapter that deduplicates image uploads via S3.
 *
 * Hashes each file in the browser (SHA-256) before uploading. If the same file
 * has already been uploaded in this editor session, the cached URL is returned
 * immediately — no network request, no new s3_object_references row.
 *
 * Upload flow:
 *   1. POST /s3/upload            — get presigned URL + token
 *   2. PUT  to presigned URL      — upload file directly to S3
 *   3. POST /s3/register-file     — register the S3 object reference
 *   4. POST /feature/s3-embed/:fid — create the embedded image property
 *
 * Usage: pass the factory returned by `createDedupUploadAdapterPlugin()` to
 * the CKEditor `extraPlugins` config array. This overrides the built-in
 * SimpleUploadAdapter.
 */

/** Session-level cache: SHA-256 hex → { default: imageUrl } */
const uploadCache = new Map<string, { default: string }>()

async function hashFile(file: File): Promise<string> {
  const buffer = await file.arrayBuffer()
  const hashBuffer = await crypto.subtle.digest('SHA-256', buffer)
  const hashArray = Array.from(new Uint8Array(hashBuffer))
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
}

class DedupUploadAdapter {
  private loader: any
  private apiBase: string
  private featureId: number
  private getToken: () => Promise<string>
  private abortController: AbortController | null = null

  constructor(loader: any, apiBase: string, featureId: number, getToken: () => Promise<string>) {
    this.loader = loader
    this.apiBase = apiBase
    this.featureId = featureId
    this.getToken = getToken
  }

  async upload(): Promise<{ default: string }> {
    const file: File = await this.loader.file

    const hash = await hashFile(file)
    const cacheKey = `${this.featureId}:${hash}`

    const cached = uploadCache.get(cacheKey)
    if (cached) {
      return cached
    }

    this.abortController = new AbortController()
    const { signal } = this.abortController

    // Fetch a fresh auth token before each upload to avoid stale tokens
    // during long editing sessions
    const token = await this.getToken()
    const authHeaders: Record<string, string> = { Authorization: `Bearer ${token}` }

    // Include access-token from localStorage to match Angular HttpClient behavior.
    // Backend middleware uses this cached JWT to avoid live Stripe/Auth0 API calls.
    const accessToken = localStorage.getItem('accessToken')
    if (accessToken) {
      authHeaders['access-token'] = accessToken
    }

    const jsonHeaders = { ...authHeaders, 'Content-Type': 'application/json' }

    // Helper: fetch with error body extraction
    const apiFetch = async (url: string, init: RequestInit, stepLabel: string) => {
      const r = await fetch(url, init)
      if (!r.ok) {
        const body = await r.text().catch(() => '')
        throw new Error(`${stepLabel} failed: ${r.status} — ${body}`)
      }
      return r
    }

    // Step 1: Get presigned upload URL
    const uploadRes = await apiFetch(`${this.apiBase}/s3/upload`, {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify({ filename: file.name, purpose: 'upload-feature-embed' }),
      signal,
    }, 'Step 1 (presign)').then(r => r.json())

    // Step 2: Upload file directly to S3, with a backend proxy fallback for
    // local preview origins that S3 CORS does not allow.
    let uploadToken = uploadRes.token
    await apiFetch(uploadRes.url, {
      method: 'PUT',
      body: file,
      signal,
    }, 'Step 2 (S3 PUT)').catch(() => {
      const formData = new FormData()
      formData.append('file', file, file.name)
      formData.append('token', uploadRes.token)
      formData.append('purpose', 'upload-feature-embed')

      return apiFetch(`${this.apiBase}/s3/upload-proxy`, {
        method: 'POST',
        headers: authHeaders,
        body: formData,
        signal,
      }, 'Step 2 (upload proxy)').then(r => r.json()).then(proxyRes => {
        uploadToken = proxyRes.token ?? uploadRes.token
      })
    })

    // Step 3: Register the S3 file reference
    const s3Ref = await apiFetch(`${this.apiBase}/s3/register-file`, {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify({ token: uploadToken, label: 'embeddedImage', size: file.size }),
      signal,
    }, 'Step 3 (register)').then(r => r.json())

    // Step 4: Create the embedded image property
    const embedRes = await apiFetch(`${this.apiBase}/feature/s3-embed/${this.featureId}`, {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify({ s3ObjectReferenceId: s3Ref.id }),
      signal,
    }, 'Step 4 (embed)').then(r => r.json())

    // The backend returns { attachment, url } — CKEditor expects { default: url }
    const result = { default: embedRes.url }
    uploadCache.set(cacheKey, result)
    return result
  }

  abort(): void {
    this.abortController?.abort()
  }
}

/**
 * Creates a CKEditor plugin function that registers the S3 dedup upload adapter.
 * Call this once with the API base URL, feature ID and a token-getter function,
 * then pass the returned function to `config.extraPlugins`.
 *
 * The `getToken` callback is invoked before each upload to ensure the auth token
 * is always fresh — avoiding stale Bearer tokens during long editing sessions.
 */
export function createDedupUploadAdapterPlugin(apiBase: string, featureId: number, getToken: () => Promise<string>) {
  return function DedupUploadAdapterPlugin(editor: any) {
    editor.plugins.get('FileRepository').createUploadAdapter = (loader: any) => {
      return new DedupUploadAdapter(loader, apiBase, featureId, getToken)
    }
  }
}

/** Clear the upload cache (e.g. when switching features) */
export function clearUploadCache(): void {
  uploadCache.clear()
}
